import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';

import { Layout, Spin, Row, Col, Card, Table, Button, Alert } from 'antd';
import { formatDateForParam, secondsToStr } from '../../utils/DataFormatterUtils';

import {
	collectObjectsWithValidPropFromTreeData,
	filterTreeData,
	flattenTreeData,
	nullSafeGet,
	removeEmptyChildrenFromTreeData,
} from '../../utils/DataAccessUtils';
import Ellipsis from 'ant-design-pro/lib/Ellipsis';
import { withRouter } from 'react-router';
import { EmptyState } from '../empty_state/EmptyState';
import moment from 'moment';
import {
	downtimeAnalyticsRestCrudThunksForBuyer,
	downtimeAnalyticsRestCrudThunksForSupplier,
} from '../../thunks/asset_analytics_thunks';
import { DOWNTIME_ANALYTICS_CRUD_ACTION_CREATORS } from '../../actions/downtime_analytics_actions';
import DowntimeAnalyticsControlBar from '../downtime_analytics_control_bar/DowntimeAnalyticsControlBar';
import { ROLE_TYPES, CSV_EXPORT_DEFAULTS } from '../../utils/DataConstants';
import { ExportToCsv } from 'export-to-csv';
import { BarChart } from '../charts/BarChart';
const queryString = require('qs');
const { Content } = Layout;

const exportReduceFunction = (obj) => ({
	name: obj.name,
	noOfAssetsOffline: obj.noOfAssetsOffline,
	meanDownTime: secondsToStr(obj.meanDownTime),
	meanTimeToRespond: secondsToStr(obj.meanTimeToRespond),
	meanTimeBetweenFailures: secondsToStr(obj.meanTimeBetweenFailures),
});

const chartConfig = (fieldName, fieldCol) => ({
	xAxisName: 'name',
	yAxisName: `${fieldName}`,
	position: `name*${fieldName}`,
	fillArea: false,
	color: 'name',
	cols: {
		fieldName: { alias: 'Manufacturer' },
		[fieldName]: fieldCol || { alias: `${fieldCol}`, formatter: (val) => secondsToStr(val || 0) },
	},
	yAxisLabel: {
		textStyle: {
			fontSize: '14',
			fontFamily: 'Roboto',
			fontWeight: 400,
			fill: 'rgba(0,0,0,0.65)',
		},
	},
	xAxisLabel: {
		textStyle: {
			fontSize: '14',
			fontFamily: 'Roboto',
			fontWeight: 400,
			fill: 'rgba(0,0,0,0.65)',
		},
	},
});

const DowntimeAnalyticsOverviewManufacturersPage: FC<any> = ({
	getOverviewAnalaytics,
	analytics,
	saveFilters,
	clearFilters,
	userType,
}): React.ReactElement => {
	const [data, setData] = useState([]);
	const [fetching, setFetching] = useState(false);
	const { filters } = analytics.groupByManufacturers;

	const startDate = filters.startDate && moment(filters.startDate);
	const endDate = filters.endDate && moment(filters.endDate);

	const fetchData = (params, pagination?, sorting?, filters?) => {
		setFetching(true);
		getOverviewAnalaytics(
			{ ...params, groupBy: 'manufacturer' },
			'groupByManufacturers',
			pagination,
			sorting,
			filters
		)
			.then((res) => {
				setData(res);
			})
			.finally(() => {
				setFetching(false);
			});
	};

	useEffect(() => {
		fetchData({}, null, null, {
			startDate: formatDateForParam(moment().subtract(1, 'months')),
			endDate: formatDateForParam(moment()),
		});
	}, []);

	const filterByManufacturers = useCallback(
		(allData) => {
			return (filters.manufacturers || []).length > 0
				? filterTreeData(allData, (obj) => (filters.manufacturers || []).includes(obj.name))
				: allData;
		},
		[filters.manufacturers]
	);

	const displayData = useMemo(() => {
		const displayData = filterByManufacturers(data || []);
		return displayData;
	}, [data, filterByManufacturers]);

	const getLink = useCallback(
		(extraParams?) => {
			const filters = analytics.groupByManufacturers.filters;
			const queryParams = queryString.stringify({
				startDate: filters.startDate && filters.startDate,
				endDate: filters.endDate && filters.endDate,
				locationIds: filters.locationIds && filters.locationIds.join(','),
				assetTypeIds: filters.assetTypeIds && filters.assetTypeIds.join(','),
				manufacturer: filters.manufacturer,
				sort_by: 'meanDownTime',
				order: 'descend',
				...extraParams,
			});
			return `/${userType}/assets/overview/downtime?${queryParams}`;
		},
		[analytics.groupByManufacturers]
	);
	const openDetails = (manufacturer) => {
		window.open(getLink({ manufacturer: manufacturer.name }), '_blank');
	};

	const columns = [
		{
			title: 'Manufacturer',
			dataIndex: 'name',
			key: 'name',
			render: (text) => (
				<Ellipsis tooltip={true} style={{ display: 'inline' }} length={28}>
					{text}
				</Ellipsis>
			),
			width: '30%',
		},
		{
			title: 'No. of Assets Down',
			dataIndex: 'noOfAssetsOffline',
			key: 'noOfAssetsOffline',
			render: (text) => text,
			sorter: (a, b) => a.noOfAssetsOffline - b.noOfAssetsOffline,
			width: '15%',
		},
		{
			title: 'Mean Downtime',
			dataIndex: 'meanDownTime',
			key: 'meanDownTime',
			render: (text) => secondsToStr(text),
			sorter: (a, b) => (a.meanDownTime || 0) - (b.meanDownTime || 0),
			defaultSortOrder: 'descend',
			width: '15%',
		},
		{
			title: 'Mean time to respond',
			dataIndex: 'meanTimeToRespond',
			key: 'meanTimeToRespond',
			render: (text) => secondsToStr(text),
			sorter: (a, b) => (a.meanTimeToRespond || 0) - (b.meanTimeToRespond || 0),
			width: '15%',
		},
		{
			title: 'Mean time between failures',
			dataIndex: 'meanTimeBetweenFailures',
			key: 'meanTimeBetweenFailures',
			render: (text) => secondsToStr(text),
			sorter: (a, b) => (a.meanTimeBetweenFailures || 0) - (b.meanTimeBetweenFailures || 0),
			width: '15%',
		},
		{
			title: '',
			dataIndex: 'action',
			key: 'action',
			render: (text, manufacturer) => (
				<Button
					type="link"
					onClick={() => {
						openDetails(manufacturer);
					}}
				>
					See Details <i className="icons8-font icons8-external-link ml-2 inline-block" />
				</Button>
			),
			width: '10%',
		},
	];

	const onDownloadAnalytics = useCallback(() => {
		const csvExporter = new ExportToCsv({
			...CSV_EXPORT_DEFAULTS,
			filename: 'Downtime Analytics by Manufacturers',
			title: 'Downtime Analytics by Manufacturers',
			headers: [
				'Manufacturer',
				'No. of Assets Down',
				'Mean Downtime',
				'Mean time to respond',
				'Mean time between failures',
			],
		});
		csvExporter.generateCsv(
			flattenTreeData(removeEmptyChildrenFromTreeData(displayData), exportReduceFunction)
		);
	}, [displayData]);
	const noOfAssetsDownCountByLocationData = useMemo(
		() =>
			collectObjectsWithValidPropFromTreeData(data, 'noOfAssetsOffline').sort(
				(a, b) => b.noOfAssetsOffline - a.noOfAssetsOffline
			),
		[data]
	);
	return (
		<Content
			className="analyticsOverviewLocationsPage"
			style={{ padding: '0 0.5em', position: 'relative' }}
		>
			{/*<BackTop/>*/}
			<Row key={1} style={{ margin: '0.5em -8px' }} gutter={16}>
				<Col span={24}>
					<div className=" mb-2 grid gap-y-3">
						<DowntimeAnalyticsControlBar
							updateFilters={(filters) => {
								if (filters.startDate && filters.endDate) {
									fetchData({}, null, null, {
										...filters,
										startDate: formatDateForParam(filters.startDate),
										endDate: formatDateForParam(filters.endDate),
									});
								} else if (startDate && endDate) {
									fetchData({ rightNow: true }, null, null, filters);
								} else {
									saveFilters({ ...filters }, 'groupByManufacturers');
								}
							}}
							clearFilters={() => clearFilters('groupByManufacturers')}
							assetTypeIds={filters.assetTypeIds}
							startDate={startDate}
							endDate={endDate}
							manufacturers={data}
							filters={filters}
							filterConfig={{
								assetTypes: true,
								manufacturer: true,
							}}
							showDownload
							onDownload={onDownloadAnalytics}
						/>
					</div>
				</Col>
			</Row>

			<>
				<Row key={2} style={{ margin: '0.5em -8px' }} gutter={16}>
					<Col span={24}>
						<Card>
							<Spin spinning={fetching}>
								{data.length > 0 ? (
									<Table
										columns={columns}
										rowKey={(el) => el.id}
										dataSource={removeEmptyChildrenFromTreeData(displayData)}
										pagination={{ pageSize: 10 }}
										// sortDirections={['descend', 'ascend']}
									/>
								) : (
									<EmptyState
										height={120}
										headline={'No manufacturers found'}
										body={
											<div style={{ maxWidth: 488, textAlign: 'center' }}>
												We didn't find any manufacturers. Try changing your filters or expanding the
												selected time period.
											</div>
										}
									/>
								)}
							</Spin>
						</Card>
					</Col>
				</Row>
				{/* <Row key={3} gutter={16} className="mt-6 mb-10 grid gap-y-4">
					<Col span={12}>
						<Card bodyStyle={{ padding: 16 }}>
							<h5>No of Assets Down </h5>
							<Spin spinning={analytics.fetching}>
								{noOfAssetsDownCountByLocationData.length > 0 ? (
									<BarChart
										height={noOfAssetsDownCountByLocationData.length * 32}
										data={noOfAssetsDownCountByLocationData.sort(
											(a, b) => parseFloat(b.noOfAssetsOffline) - parseFloat(a.noOfAssetsOffline)
										)}
										showTooltipTitle={false}
										tooltipItemTemplate={`
										<li data-index={index}>
												<!-- The marker for each record -->
												<span style="background-color:{color};width:8px;height:8px;border-radius:50%;display:inline-block;margin-right:8px;"></span>
												{name}: {value} assets down 
										</li>`}
										xAxisName={nullSafeGet(
											'xAxisName',
											chartConfig('noOfAssetsOffline', { alias: 'No of Assets Down' })
										)}
										yAxisName={nullSafeGet(
											'yAxisName',
											chartConfig('noOfAssetsOffline', { alias: 'No of Assets Down' })
										)}
										xAxisLabel={nullSafeGet(
											'xAxisLabel',
											chartConfig('noOfAssetsOffline', { alias: 'No of Assets Down' })
										)}
										yAxisLabel={nullSafeGet(
											'yAxisLabel',
											chartConfig('noOfAssetsOffline', { alias: 'No of Assets Down' })
										)}
										fillArea={nullSafeGet(
											'fillArea',
											chartConfig('noOfAssetsOffline', { alias: 'No of Assets Down' })
										)}
										cols={nullSafeGet(
											'cols',
											chartConfig('noOfAssetsOffline', { alias: 'No of Assets Down' })
										)}
										color={nullSafeGet(
											'color',
											chartConfig('noOfAssetsOffline', { alias: 'No of Assets Down' })
										)}
										position={nullSafeGet(
											'position',
											chartConfig('noOfAssetsOffline', { alias: 'No of Assets Down' })
										)}
									/>
								) : (
									<EmptyState
										height={120}
										headline={'No locations found'}
										body={
											<div style={{ maxWidth: 488, textAlign: 'center' }}>
												We didn't find any locations. Try changing your filters or expanding the
												selected time period.
											</div>
										}
									/>
								)}
							</Spin>
						</Card>
					</Col>
					<Col span={12}>
						<Card bodyStyle={{ padding: 16 }}>
							<h5>Mean Downtime </h5>
							<Spin spinning={analytics.fetching}>
								{noOfAssetsDownCountByLocationData.length > 0 ? (
									<BarChart
										height={noOfAssetsDownCountByLocationData.length * 32}
										chartPadding={[40, 40, 40, 200]}
										data={noOfAssetsDownCountByLocationData.sort(
											(a, b) => b.meanDownTime - a.meanDownTime
										)}
										showTooltipTitle={false}
										tooltipItemTemplate={`
										<li data-index={index}>
												<!-- The marker for each record -->
												<span style="background-color:{color};width:8px;height:8px;border-radius:50%;display:inline-block;margin-right:8px;"></span>
												{name}: {value} Mean Downtime 
										</li>`}
										xAxisName={nullSafeGet(
											'xAxisName',
											chartConfig('meanDownTime', {
												alias: 'Mean Downtime',
												formatter: (val) => secondsToStr(val || 0),
											})
										)}
										yAxisName={nullSafeGet(
											'yAxisName',
											chartConfig('meanDownTime', {
												alias: 'Mean Downtime',
												formatter: (val) => secondsToStr(val || 0),
											})
										)}
										xAxisLabel={nullSafeGet(
											'xAxisLabel',
											chartConfig('meanDownTime', {
												alias: 'Mean Downtime',
												formatter: (val) => secondsToStr(val || 0),
											})
										)}
										yAxisLabel={nullSafeGet(
											'yAxisLabel',
											chartConfig('meanDownTime', {
												alias: 'Mean Downtime',
												formatter: (val) => secondsToStr(val || 0),
											})
										)}
										fillArea={nullSafeGet(
											'fillArea',
											chartConfig('meanDownTime', {
												alias: 'Mean Downtime',
												formatter: (val) => secondsToStr(val || 0),
											})
										)}
										cols={nullSafeGet(
											'cols',
											chartConfig('meanDownTime', {
												alias: 'Mean Downtime',
												formatter: (val) => secondsToStr(val || 0),
											})
										)}
										color={nullSafeGet(
											'color',
											chartConfig('meanDownTime', {
												alias: 'Mean Downtime',
												formatter: (val) => secondsToStr(val || 0),
											})
										)}
										position={nullSafeGet(
											'position',
											chartConfig('meanDownTime', {
												alias: 'Mean Downtime',
												formatter: (val) => secondsToStr(val || 0),
											})
										)}
									/>
								) : (
									<EmptyState
										height={120}
										headline={'No locations found'}
										body={
											<div style={{ maxWidth: 488, textAlign: 'center' }}>
												We didn't find any locations. Try changing your filters or expanding the
												selected time period.
											</div>
										}
									/>
								)}
							</Spin>
						</Card>
					</Col>
					<Col span={12}>
						<Card bodyStyle={{ padding: 16 }}>
							<h5>Mean time to respond </h5>
							<Spin spinning={analytics.fetching}>
								{noOfAssetsDownCountByLocationData.length > 0 ? (
									<BarChart
										height={noOfAssetsDownCountByLocationData.length * 32}
										chartPadding={[40, 40, 40, 200]}
										data={noOfAssetsDownCountByLocationData.sort(
											(a, b) => b.meanTimeToRespond - a.meanTimeToRespond
										)}
										showTooltipTitle={false}
										tooltipItemTemplate={`
										<li data-index={index}>
												<!-- The marker for each record -->
												<span style="background-color:{color};width:8px;height:8px;border-radius:50%;display:inline-block;margin-right:8px;"></span>
												{name}: {value} Mean time to respond 
										</li>`}
										xAxisName={nullSafeGet(
											'xAxisName',
											chartConfig('meanTimeToRespond', {
												alias: 'Mean time to respond',
												formatter: (val) => secondsToStr(val || 0),
											})
										)}
										yAxisName={nullSafeGet(
											'yAxisName',
											chartConfig('meanTimeToRespond', {
												alias: 'Mean time to respond',
												formatter: (val) => secondsToStr(val || 0),
											})
										)}
										xAxisLabel={nullSafeGet(
											'xAxisLabel',
											chartConfig('meanTimeToRespond', {
												alias: 'Mean time to respond',
												formatter: (val) => secondsToStr(val || 0),
											})
										)}
										yAxisLabel={nullSafeGet(
											'yAxisLabel',
											chartConfig('meanTimeToRespond', {
												alias: 'Mean time to respond',
												formatter: (val) => secondsToStr(val || 0),
											})
										)}
										fillArea={nullSafeGet(
											'fillArea',
											chartConfig('meanTimeToRespond', {
												alias: 'Mean time to respond',
												formatter: (val) => secondsToStr(val || 0),
											})
										)}
										cols={nullSafeGet(
											'cols',
											chartConfig('meanTimeToRespond', {
												alias: 'Mean time to respond',
												formatter: (val) => secondsToStr(val || 0),
											})
										)}
										color={nullSafeGet(
											'color',
											chartConfig('meanTimeToRespond', {
												alias: 'Mean time to respond',
												formatter: (val) => secondsToStr(val || 0),
											})
										)}
										position={nullSafeGet(
											'position',
											chartConfig('meanTimeToRespond', {
												alias: 'Mean time to respond',
												formatter: (val) => secondsToStr(val || 0),
											})
										)}
									/>
								) : (
									<EmptyState
										height={120}
										headline={'No locations found'}
										body={
											<div style={{ maxWidth: 488, textAlign: 'center' }}>
												We didn't find any locations. Try changing your filters or expanding the
												selected time period.
											</div>
										}
									/>
								)}
							</Spin>
						</Card>
					</Col>
					<Col span={12}>
						<Card bodyStyle={{ padding: 16 }}>
							<h5>Mean time between failures </h5>
							<Spin spinning={analytics.fetching}>
								{noOfAssetsDownCountByLocationData.length > 0 ? (
									<BarChart
										height={noOfAssetsDownCountByLocationData.length * 32}
										chartPadding={[40, 40, 40, 200]}
										data={noOfAssetsDownCountByLocationData.sort(
											(a, b) => b.meanTimeBetweenFailures - a.meanTimeBetweenFailures
										)}
										showTooltipTitle={false}
										tooltipItemTemplate={`
										<li data-index={index}>
												<!-- The marker for each record -->
												<span style="background-color:{color};width:8px;height:8px;border-radius:50%;display:inline-block;margin-right:8px;"></span>
												{name}: {value} Mean time between failures 
										</li>`}
										xAxisName={nullSafeGet(
											'xAxisName',
											chartConfig('meanTimeBetweenFailures', {
												alias: 'Mean time between failures',
												formatter: (val) => secondsToStr(val || 0),
											})
										)}
										yAxisName={nullSafeGet(
											'yAxisName',
											chartConfig('meanTimeBetweenFailures', {
												alias: 'Mean time between failures',
												formatter: (val) => secondsToStr(val || 0),
											})
										)}
										xAxisLabel={nullSafeGet(
											'xAxisLabel',
											chartConfig('meanTimeBetweenFailures', {
												alias: 'Mean time between failures',
												formatter: (val) => secondsToStr(val || 0),
											})
										)}
										yAxisLabel={nullSafeGet(
											'yAxisLabel',
											chartConfig('meanTimeBetweenFailures', {
												alias: 'Mean time between failures',
												formatter: (val) => secondsToStr(val || 0),
											})
										)}
										fillArea={nullSafeGet(
											'fillArea',
											chartConfig('meanTimeBetweenFailures', {
												alias: 'Mean time between failures',
												formatter: (val) => secondsToStr(val || 0),
											})
										)}
										cols={nullSafeGet(
											'cols',
											chartConfig('meanTimeBetweenFailures', {
												alias: 'Mean time between failures',
												formatter: (val) => secondsToStr(val || 0),
											})
										)}
										color={nullSafeGet(
											'color',
											chartConfig('meanTimeBetweenFailures', {
												alias: 'Mean time between failures',
												formatter: (val) => secondsToStr(val || 0),
											})
										)}
										position={nullSafeGet(
											'position',
											chartConfig('meanTimeBetweenFailures', {
												alias: 'Mean time between failures',
												formatter: (val) => secondsToStr(val || 0),
											})
										)}
									/>
								) : (
									<EmptyState
										height={120}
										headline={'No locations found'}
										body={
											<div style={{ maxWidth: 488, textAlign: 'center' }}>
												We didn't find any locations. Try changing your filters or expanding the
												selected time period.
											</div>
										}
									/>
								)}
							</Spin>
						</Card>
					</Col>
				</Row> */}
			</>
		</Content>
	);
};

const mapStateToProps = (state, ownProps) => ({
	history: ownProps.history,
	currentUser: state.session.currentUser,
	analytics: state.downtime_analytics,
	userType: (state as any).session.userType,
	locations: state.locations,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	saveFilters: (filters, targetCollectionName) =>
		dispatch(DOWNTIME_ANALYTICS_CRUD_ACTION_CREATORS.updateFilters(filters, targetCollectionName)),
	clearAllFilters: (targetCollectionName) =>
		dispatch(DOWNTIME_ANALYTICS_CRUD_ACTION_CREATORS.clearAllFilters(targetCollectionName)),
	getOverviewAnalaytics: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? downtimeAnalyticsRestCrudThunksForSupplier.read(
						params,
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
				  )
				: downtimeAnalyticsRestCrudThunksForBuyer.read(
						params,
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
				  )
		),
});

const ComponentWithoutUserType = withRouter(
	connect(mapStateToProps, mapDispatchToProps)(DowntimeAnalyticsOverviewManufacturersPage)
);

export default connect(
	(state) => ({
		userType: (state as any).session.userType,
	}),
	() => ({})
)(ComponentWithoutUserType);
